import AlertCircle from "@untitled-ui/icons-react/build/esm/AlertCircle";
import AlertTriangle from "@untitled-ui/icons-react/build/esm/AlertTriangle";
import List from "@untitled-ui/icons-react/build/esm/List";
import Plus from "@untitled-ui/icons-react/build/esm/Plus";
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import BtnLargeSolid from "../../../../common/buttons/BtnLargeSolid";
import CheckboxVector from "../../../../common/checkbox/CheckboxVector";
import FormField from "../../../../common/forms/FormField";
import SimpleLoadingSpinner from "../../../../common/loaders/SimpleLoadingSpinner";
import FullScreenOverlay from "../../../../common/overlay/FullScreenOverlay";
import TooltipSimple, { TooltipTitle } from "../../../../common/tooltip/TooltipSimple";
import TooltipWithInfoCircle from "../../../../common/tooltip/TooltipWithInfoCircle";
import {
    buildCategorySizeSourceRows,
    groupCategoryMappings,
    updateCategoryMappingGroup
} from "../categoryMappingState";
import { getCategorySizeCountIssues } from "../sizeGroupConversionState";
import {
    findCategoryMapping,
    getCategoryMappingKey,
    getCategorySourceKey,
    sourceValuesMatch
} from "../sourceMappingIdentity";
import POSSourcePreview from "./POSSourcePreview";

// #region CONSTANTS
const EMPTY_ARRAY = [];

const SOURCE_FIELD_OPTIONS = [
    { name: "Category", value: "labelBrandCategories" },
    { name: "Product type", value: "labelBrandProductTypes" }
];

const INCOMPLETE_MAPPINGS_MESSAGE =
    "SAVE is only enabled when all of the brand's categories are linked to a POS category and Size System!";
const DEFAULT_SIZE_SYSTEMS_MESSAGE =
    "Some of the Size Systems shown here come from My Defaults. Please SAVE to apply them to this brand's rules.";
const GROUPED_MAPPING_CONFLICT_MESSAGE =
    "Some brand genders share a POS gender and brand size group but have different mappings. Select a common POS category and Size System for each combined row before saving.";

const valueIsPresent = value => value !== undefined && value !== null && value !== "";

const hasOwnProperty = (object, propertyName) => !!object && Object.prototype.hasOwnProperty.call(object, propertyName);

const hasSizeSystemFields = categoryMapping =>
    hasOwnProperty(categoryMapping, "relatedSizeGroupIdFromPOS") ||
    hasOwnProperty(categoryMapping, "relatedSizeGroupNameFromPOS");

const hasCompleteSizeSystem = categoryMapping =>
    valueIsPresent(categoryMapping?.relatedSizeGroupIdFromPOS) &&
    valueIsPresent(categoryMapping?.relatedSizeGroupNameFromPOS);

const sizeSystemValuesMatch = (valueA, valueB) => String(valueA ?? "") === String(valueB ?? "");

const formatSizeGroupSizes = sizes =>
    (Array.isArray(sizes) ? sizes : EMPTY_ARRAY)
        .map(String)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }))
        .join(", ");

const getSizeCountIssueTooltip = (category, issue, categoryFieldName) => {
    const gender = category.posGender ? ` (${category.posGender})` : "";
    const sizeSystemName = category.relatedSizeGroupNameFromPOS;
    const brandSizes = category.labelBrandSizeGroupSizes?.length
        ? category.labelBrandSizeGroupSizes
        : issue.missingSizeNames;

    return (
        <>
            <div>
                {categoryFieldName} "{category.labelBrandCategoryName}"{gender} is assigned to POS Size System
                {` "${sizeSystemName}"`}, which does not have enough sizes to cover all brand's size group.
            </div>
            <ul className="category-size-count-list">
                <li>
                    Brand's Size Group: {formatSizeGroupSizes(brandSizes) || "--"} (total: {brandSizes?.length ?? 0})
                </li>
                <li>
                    Sizes in the assigned POS Size System: {formatSizeGroupSizes(issue.posSizeNames) || "--"} (total:{" "}
                    {issue.posSizeNames?.length ?? 0})
                </li>
            </ul>
        </>
    );
};

const getPOSCategoryOptionValue = (category, index) => {
    const categoryID = category?.categoryIdFromPOS ?? category?.id;
    return `pos-category-${valueIsPresent(categoryID) ? categoryID : "missing-id"}-${index}`;
};

const findSavedPOSCategoryOption = (savedCategoryMapping, posCategoryOptions) => {
    if (!savedCategoryMapping) return null;

    const hasSubCategoryMapping = Object.prototype.hasOwnProperty.call(savedCategoryMapping, "subCategoryExportName");

    return (
        posCategoryOptions.find(option => {
            if (option.categoryNameFromPOS !== savedCategoryMapping.exportName) return false;
            if (!hasSubCategoryMapping) return true;

            return (option.subCategoryNameFromPOS ?? "") === (savedCategoryMapping.subCategoryExportName ?? "");
        }) ||
        posCategoryOptions.find(option => option.name === savedCategoryMapping.exportName) ||
        null
    );
};

const findAIPosCategoryOption = (aiCategoryMatch, posCategoryOptions) => {
    if (!aiCategoryMatch?.posCategoryName) return null;

    const posSubCategoryName = aiCategoryMatch?.posSubCategoryName ?? "";
    return (
        posCategoryOptions.find(
            option =>
                option?.categoryNameFromPOS === aiCategoryMatch.posCategoryName &&
                (option?.subCategoryNameFromPOS ?? "") === posSubCategoryName
        ) || null
    );
};

const findAICategoryMatch = ({
    categorySourceName,
    item,
    labelBrandCategoriesMatches = EMPTY_ARRAY,
    labelBrandProductTypesMatches = EMPTY_ARRAY
}) => {
    const labelBrandName = item?.name ?? "";
    const labelBrandGenderName = item?.gender ?? "";
    const rawMatches =
        categorySourceName === "Product type" ? labelBrandProductTypesMatches : labelBrandCategoriesMatches;
    const matches = Array.isArray(rawMatches) ? rawMatches : [];
    const sourceNameKey = categorySourceName === "Product type" ? "productTypeName" : "categoryName";

    return (
        matches.find(
            match =>
                getCategorySourceKey(match?.[sourceNameKey], match?.gender) ===
                    getCategorySourceKey(labelBrandName, labelBrandGenderName) && !!match?.posCategoryName
        ) || null
    );
};

const buildCategoryDraftFromOption = (category, option) => ({
    ...category,
    exportName: option?.categoryNameFromPOS ?? "",
    subCategoryExportName: option?.subCategoryNameFromPOS ?? "",
    completeCategoryNameFromPOS: option?.completeCategoryNameFromPOS ?? option?.name ?? "",
    relatedSizeGroupIdFromPOS: option?.relatedSizeGroupIdFromPOS ?? null,
    relatedSizeGroupNameFromPOS: option?.relatedSizeGroupNameFromPOS ?? ""
});

const buildCategoryDraftFromSizeSystem = (category, sizeSystemOption) => ({
    ...category,
    relatedSizeGroupIdFromPOS: sizeSystemOption?.value ?? null,
    relatedSizeGroupNameFromPOS: sizeSystemOption?.name ?? ""
});

const buildCategoryRows = ({
    sourceItems = EMPTY_ARRAY,
    labelBrandProducts = EMPTY_ARRAY,
    genderMappings = EMPTY_ARRAY,
    categoryMappings = EMPTY_ARRAY,
    posCategoryOptions = EMPTY_ARRAY,
    categorySourceName,
    categoryMappingsNeedConfirmation = false,
    labelBrandCategoriesMatches = EMPTY_ARRAY,
    labelBrandProductTypesMatches = EMPTY_ARRAY,
    includeCheckboxState
}) =>
    buildCategorySizeSourceRows({
        sourceItems,
        genderMappings,
        labelBrandProducts,
        categoryFieldName: categorySourceName
    }).map(item => {
        const labelBrandCategoryName = item?.name ?? "";
        const labelBrandGenderName = item?.gender ?? "";
        const savedGenderMapping = genderMappings.find(mapping =>
            sourceValuesMatch(mapping?.originalGenderName, labelBrandGenderName)
        );
        const savedCategoryMapping = findCategoryMapping(
            categoryMappings,
            labelBrandCategoryName,
            labelBrandGenderName,
            item.labelBrandSizeGroupSizes
        );
        const aiCategoryMatch =
            categoryMappingsNeedConfirmation && !savedCategoryMapping
                ? findAICategoryMatch({
                      categorySourceName,
                      item,
                      labelBrandCategoriesMatches,
                      labelBrandProductTypesMatches
                  })
                : null;
        const savedPOSCategoryOption = findSavedPOSCategoryOption(savedCategoryMapping, posCategoryOptions);
        const aiPOSCategoryOption = findAIPosCategoryOption(aiCategoryMatch, posCategoryOptions);
        const selectedPOSCategoryOption = savedPOSCategoryOption || aiPOSCategoryOption;
        const useSavedSizeSystem = hasSizeSystemFields(savedCategoryMapping);

        const row = {
            labelBrandCategoryName,
            labelBrandGenderName,
            labelBrandSizeGroupSizes: item.labelBrandSizeGroupSizes,
            posGender: savedGenderMapping?.userGenderName || labelBrandGenderName || "-",
            exportName: selectedPOSCategoryOption?.categoryNameFromPOS ?? savedCategoryMapping?.exportName ?? "",
            subCategoryExportName:
                selectedPOSCategoryOption?.subCategoryNameFromPOS ?? savedCategoryMapping?.subCategoryExportName ?? "",
            completeCategoryNameFromPOS:
                selectedPOSCategoryOption?.name ??
                savedCategoryMapping?.completeCategoryNameFromPOS ??
                savedCategoryMapping?.exportName ??
                "",
            relatedSizeGroupIdFromPOS:
                (useSavedSizeSystem
                    ? savedCategoryMapping?.relatedSizeGroupIdFromPOS
                    : selectedPOSCategoryOption?.relatedSizeGroupIdFromPOS) ?? null,
            relatedSizeGroupNameFromPOS:
                (useSavedSizeSystem
                    ? savedCategoryMapping?.relatedSizeGroupNameFromPOS
                    : selectedPOSCategoryOption?.relatedSizeGroupNameFromPOS) ?? ""
        };

        return includeCheckboxState ? { ...row, isChecked: false } : row;
    });
// #endregion

// #region SCOPED STYLES
const StyledPOSCategoriesTablePopup = styled.div`
    & {
        width: 1280px;
        max-width: calc(100vw - 48px);
        overflow-x: auto;
        text-align: start;
    }

    .popup-shell {
        min-width: 1200px;
        box-sizing: border-box;
        background: var(--backgrounds-and-lines-n-400, #1e2631);
        border-radius: 8px;
        padding: 12px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        align-items: center;
    }

    .popup-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        align-self: stretch;
        width: 100%;
        padding-top: 9px;
        padding-bottom: 21px;
        border-bottom: 1px solid var(--backgrounds-and-lines-n-100, #3c485a);
        margin-bottom: 8px;
    }

    .header-title-container,
    .header-actions,
    .header-label {
        display: flex;
        align-items: center;
    }

    .header-title-container {
        gap: 8px;
    }

    .header-actions {
        gap: 8px;
    }

    .header-icon {
        width: 16px;
        height: 16px;
        color: var(--text-and-icons-green-500, #00bb00);
    }

    .header-title {
        color: var(--text-and-icons-green-500, #00bb00);
        font-size: 14px;
        font-weight: 400;
        text-transform: uppercase;
        position: relative;
        top: 1px;
    }

    .popup-content {
        display: flex;
        flex-direction: column;
        gap: 14px;
        align-self: stretch;
        width: 100%;
    }

    .bulk-action-section {
        display: flex;
        flex-direction: column;
        gap: 10px;
        align-self: stretch;
    }

    .bulk-action-row {
        display: grid;
        grid-template-columns: 305px 200px 30px;
        justify-content: center;
        gap: 10px;
        align-items: center;
        padding: 0 8px;
    }

    .bulk-label {
        color: var(--text-and-icons-gray-300, #4f5b6d);
        text-align: right;
        font-size: 14px;
        font-weight: 400;
    }

    .bulk-add-btn {
        background: var(--buttons-all-platforms-gray-500, #10141b);
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 29px;
        height: 27px;
        flex-shrink: 0;
    }

    .bulk-add-btn.enabled:hover {
        box-shadow: 0px 0px 0px 1px #ff8c00;
    }

    .plus {
        width: 16px;
        height: 16px;
    }

    .save-warning {
        color: var(--text-and-icons-orange-500, #ff9900);
        font-size: 12px;
        font-weight: 400;
        text-align: center;
        align-self: stretch;
    }

    .category-size-count-message {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        color: var(--text-and-icons-orange-500, #ff9900);
        font-size: 12px;
    }

    .category-size-count-icon {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
        color: var(--text-and-icons-orange-500, #ff9900);
    }

    .category-size-count-details {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .category-size-count-list {
        margin: 8px 0 0;
        padding-left: 16px;
    }

    .category-size-system-cell {
        display: flex;
        align-items: center;
        gap: 6px;
        min-width: 0;
    }

    .category-size-system-field {
        flex: 1;
        min-width: 0;
    }

    .categories-grid-row {
        display: grid;
        grid-template-columns: 36px minmax(140px, 1fr) 120px 44ch 240px 150px;
        font-size: 14px;
        gap: 10px;
        align-items: center;
        align-self: stretch;
        width: 100%;
        box-sizing: border-box;
        padding: 8px;
    }

    .checkbox-cell {
        display: flex;
        align-items: center;
        justify-content: flex-start;
    }

    .header-label {
        gap: 4px;
        color: var(--text-and-icons-gray-100, #d1d1d1);
        font-size: 14px;
        font-weight: 400;
    }

    .divider,
    .divider-2 {
        margin-top: -1px;
        border-style: solid;
        border-color: var(--backgrounds-and-lines-n-100, #3c485a);
        border-width: 1px 0 0 0;
        width: 100%;
        height: 0px;
    }

    .divider-2 {
        border-color: #262e3e;
    }

    .table-scroll-area {
        display: flex;
        align-self: stretch;
        overflow-y: auto;
        max-height: 520px;
    }

    .table-body {
        display: flex;
        flex-direction: column;
        gap: 8px;
        flex: 1;
    }

    .table-item {
        border-radius: 4px;
        display: flex;
        align-items: center;
        //min-height: 35px;
    }

    .table-item.even {
        background: var(--backgrounds-and-lines-n-300, #1a222d);
    }

    .item-name {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        text-align: left;
        font-size: 14px;
        font-weight: 400;
    }

    .brand-size-group {
        display: inline-block;
        justify-self: start;
        max-width: 100%;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .empty-categories-state {
        display: flex;
        flex-direction: column;
        gap: 16px;
        align-items: flex-start;
        align-self: stretch;
        padding: 4px 0px 8px 0px;
    }

    .empty-categories-title {
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--text-and-icons-orange-500, #ff9900);
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
    }

    .empty-categories-icon {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
    }

    .empty-categories-description {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        font-size: 14px;
        font-weight: 400;
        line-height: 16px;
    }

    /* Keep the natural height, with room to scroll on short viewports. */
    & {
        display: flex;
        flex-direction: column;
        min-height: 0;
        max-height: calc(100vh - 32px);
        max-height: calc(100dvh - 32px);
    }

    .popup-shell {
        min-height: 0;
        overflow-y: auto;
    }

    .popup-shell > * {
        flex-shrink: 0;
    }

    .table-scroll-area {
        flex-shrink: 1;
        /* Retain a readable scroll target if controls exceed the viewport. */
        min-height: 44px;
    }

    .table-body {
        align-self: flex-start;
    }

    .table-body > * {
        flex-shrink: 0;
    }
`;

const StyledCategorySourceStep = styled.div`
    & {
        width: 360px;
        text-align: center;
    }

    .source-shell {
        background: var(--backgrounds-and-lines-n-400, #1e2631);
        border-radius: 4px;
        padding: 16px 24px;
        display: flex;
        flex-direction: column;
        gap: 16px;
        align-items: center;
    }

    .attention-label {
        color: var(--text-and-icons-orange-500, #ff9900);
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        align-self: stretch;
        padding-bottom: 11px;
        border-bottom: 1px solid var(--backgrounds-and-lines-n-100, #3c485a);
    }

    .source-icon {
        width: 46px;
        height: 46px;
        color: var(--text-and-icons-orange-500, #ff9900);
        margin-top: 2px;
    }

    .source-title {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        font-size: 18px;
        font-weight: 700;
        line-height: 22px;
    }

    .source-description,
    .source-helper {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        font-size: 14px;
        font-weight: 400;
        line-height: normal;
    }

    .source-helper {
        max-width: 300px;
    }

    .source-actions {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 10px;
        align-self: stretch;
        margin-top: 1px;
    }

    .source-dropdown {
        align-self: stretch;
        margin-top: 1px;
    }
`;
// #endregion

export default function POSCategoriesTablePopup({
    show,
    onClose,
    genderMappings = EMPTY_ARRAY,
    categoryFieldName,
    setCategoryFieldName,
    categoryMappings = EMPTY_ARRAY,
    relatedSizeGroupMappings = EMPTY_ARRAY,
    categoryMappingsNeedConfirmation = false,
    setCategoryMappings,
    setSaveButtonEnabled,
    posExportRequestData,
    labelBrandID
}) {
    // #region STATES
    const [selectedSourceField, setSelectedSourceField] = useState(null);
    const [confirmedSourceField, setConfirmedSourceField] = useState(null);
    const [draftCategoryMappings, setDraftCategoryMappings] = useState([]);
    const [bulkCategoryOption, setBulkCategoryOption] = useState(null);
    const [bulkChangeBtnEnabled, setBulkChangeBtnEnabled] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showSourcePreview, setShowSourcePreview] = useState(false);

    const [forceConfirmSourceField, setForceConfirmSourceField] = useState(false);
    // #endregion

    // #region COMPUTED PROPS
    const labelBrandProducts = Array.isArray(posExportRequestData?.labelBrandProducts)
        ? posExportRequestData.labelBrandProducts
        : EMPTY_ARRAY;
    const labelBrandCategories = Array.isArray(posExportRequestData?.labelBrandCategories)
        ? posExportRequestData.labelBrandCategories
        : EMPTY_ARRAY;
    const labelBrandProductTypes = Array.isArray(posExportRequestData?.labelBrandProductTypes)
        ? posExportRequestData.labelBrandProductTypes
        : EMPTY_ARRAY;
    const categoriesFromPOS = Array.isArray(posExportRequestData?.categoriesFromPOS)
        ? posExportRequestData.categoriesFromPOS
        : EMPTY_ARRAY;
    const sizeGroupsFromPOS = Array.isArray(posExportRequestData?.sizeGroupsFromPOS)
        ? posExportRequestData.sizeGroupsFromPOS
        : EMPTY_ARRAY;
    const labelBrandCategoriesMatches = Array.isArray(posExportRequestData?.labelBrandCategoriesMatches)
        ? posExportRequestData.labelBrandCategoriesMatches
        : EMPTY_ARRAY;
    const labelBrandProductTypesMatches = Array.isArray(posExportRequestData?.labelBrandProductTypesMatches)
        ? posExportRequestData.labelBrandProductTypesMatches
        : EMPTY_ARRAY;
    const savedRelatedSizeGroupMappings = Array.isArray(relatedSizeGroupMappings)
        ? relatedSizeGroupMappings
        : EMPTY_ARRAY;
    const showNoDataMessage = !labelBrandProducts?.length;

    const savedSourceField = useMemo(
        () => SOURCE_FIELD_OPTIONS.find(option => option.name === categoryFieldName) || null,
        [categoryFieldName]
    );
    let effectiveCategoryFieldName = confirmedSourceField?.name || savedSourceField?.name || null;
    if (forceConfirmSourceField) effectiveCategoryFieldName = null;
    const sourceFieldSelection =
        selectedSourceField || (forceConfirmSourceField ? confirmedSourceField || savedSourceField : null);

    const showSourceStep = !showNoDataMessage && !effectiveCategoryFieldName;
    const showMappingStep = !showNoDataMessage && !!effectiveCategoryFieldName;

    const posCategoryOptions = useMemo(() => {
        return categoriesFromPOS.map((category, index) => ({
            id: category?.categoryIdFromPOS ?? category?.id,
            name: category?.completeCategoryNameFromPOS ?? category?.name ?? "",
            // DropdownOverlay uses value as its React key; POS category display names are not guaranteed to be unique.
            value: getPOSCategoryOptionValue(category, index),
            categoryNameFromPOS: category?.categoryNameFromPOS ?? category?.name ?? "",
            subCategoryNameFromPOS: category?.subCategoryNameFromPOS ?? "",
            completeCategoryNameFromPOS: category?.completeCategoryNameFromPOS ?? category?.name ?? "",
            relatedSizeGroupIdFromPOS: category?.relatedSizeGroupIdFromPOS ?? null,
            relatedSizeGroupNameFromPOS: category?.relatedSizeGroupNameFromPOS ?? ""
        }));
    }, [categoriesFromPOS]);

    const sizeSystemOptions = useMemo(() => {
        const availableSizeGroups = Array.isArray(sizeGroupsFromPOS) ? sizeGroupsFromPOS : EMPTY_ARRAY;
        const options = availableSizeGroups.map(sizeGroup => ({
            value: sizeGroup?.sizeGroupID,
            name: sizeGroup?.sizeGroupName ?? ""
        }));

        draftCategoryMappings.forEach(category => {
            if (!hasCompleteSizeSystem(category)) return;

            const selectionExists = options.some(option =>
                sizeSystemValuesMatch(option?.value, category.relatedSizeGroupIdFromPOS)
            );
            if (selectionExists) return;

            options.push({
                value: category.relatedSizeGroupIdFromPOS,
                name: category.relatedSizeGroupNameFromPOS
            });
        });

        return options;
    }, [draftCategoryMappings, sizeGroupsFromPOS]);

    const sourceCategoryRows = useMemo(() => {
        if (!effectiveCategoryFieldName) return [];

        const sourceItems =
            effectiveCategoryFieldName === "Product type" ? labelBrandProductTypes : labelBrandCategories;

        return buildCategoryRows({
            sourceItems,
            labelBrandProducts,
            genderMappings,
            categoryMappings,
            posCategoryOptions,
            categorySourceName: effectiveCategoryFieldName,
            categoryMappingsNeedConfirmation,
            labelBrandCategoriesMatches,
            labelBrandProductTypesMatches,
            includeCheckboxState: true
        });
    }, [
        categoryMappings,
        categoryMappingsNeedConfirmation,
        effectiveCategoryFieldName,
        genderMappings,
        labelBrandCategories,
        labelBrandCategoriesMatches,
        labelBrandProductTypes,
        labelBrandProductTypesMatches,
        labelBrandProducts,
        posCategoryOptions
    ]);

    const persistedSourceRows = useMemo(() => {
        if (!savedSourceField) return [];

        const sourceItems = savedSourceField.name === "Product type" ? labelBrandProductTypes : labelBrandCategories;

        return buildCategoryRows({
            sourceItems,
            labelBrandProducts,
            genderMappings,
            categoryMappings,
            posCategoryOptions,
            categorySourceName: savedSourceField.name,
            includeCheckboxState: false
        });
    }, [
        categoryMappings,
        genderMappings,
        labelBrandCategories,
        labelBrandProductTypes,
        labelBrandProducts,
        posCategoryOptions,
        savedSourceField
    ]);

    const groupedCategoryMappings = useMemo(
        () => groupCategoryMappings(draftCategoryMappings),
        [draftCategoryMappings]
    );
    const hasGroupedMappingConflicts = groupedCategoryMappings.some(
        group => group.hasCategoryConflict || group.hasSizeSystemConflict
    );
    const hasChecked = draftCategoryMappings.some(category => category.isChecked);
    const allChecked = draftCategoryMappings.length > 0 && draftCategoryMappings.every(category => category.isChecked);
    const sourceFieldChanged = !!effectiveCategoryFieldName && effectiveCategoryFieldName !== categoryFieldName;
    const mappingsChanged =
        draftCategoryMappings.length !== persistedSourceRows.length ||
        draftCategoryMappings.some((category, idx) => {
            const savedCategory = persistedSourceRows[idx];

            return (
                category.labelBrandCategoryName !== savedCategory?.labelBrandCategoryName ||
                category.labelBrandGenderName !== savedCategory?.labelBrandGenderName ||
                category.exportName !== savedCategory?.exportName ||
                (category.subCategoryExportName ?? "") !== (savedCategory?.subCategoryExportName ?? "") ||
                (category.completeCategoryNameFromPOS ?? "") !== (savedCategory?.completeCategoryNameFromPOS ?? "") ||
                !sizeSystemValuesMatch(category.relatedSizeGroupIdFromPOS, savedCategory?.relatedSizeGroupIdFromPOS) ||
                (category.relatedSizeGroupNameFromPOS ?? "") !== (savedCategory?.relatedSizeGroupNameFromPOS ?? "")
            );
        });
    const isDirty = sourceFieldChanged || mappingsChanged;
    const sizeGroupMetadataNeedsSave = sourceCategoryRows.some(
        category =>
            !categoryMappings.some(
                saved =>
                    Array.isArray(saved.labelBrandSizeGroupSizes) &&
                    getCategoryMappingKey(saved) === getCategoryMappingKey(category)
            )
    );
    const sizeCountIssues = useMemo(
        () =>
            getCategorySizeCountIssues({
                categoryFieldName: effectiveCategoryFieldName,
                categoryMappings: draftCategoryMappings,
                labelBrandProducts,
                sizeGroupsFromPOS
            }),
        [labelBrandProducts, effectiveCategoryFieldName, draftCategoryMappings, sizeGroupsFromPOS]
    );
    const hasSizeCountIssues = sizeCountIssues.some(Boolean);
    const allCategoriesMapped =
        groupedCategoryMappings.length > 0 &&
        groupedCategoryMappings.every(({ category }) => category.completeCategoryNameFromPOS || category.exportName);
    const allSizeSystemsMapped =
        groupedCategoryMappings.length > 0 &&
        groupedCategoryMappings.every(({ category }) => hasCompleteSizeSystem(category));
    const allSizeSystemsSavedForBrand =
        draftCategoryMappings.length > 0 &&
        draftCategoryMappings.every(category =>
            valueIsPresent(
                findCategoryMapping(
                    savedRelatedSizeGroupMappings,
                    category.labelBrandCategoryName,
                    category.labelBrandGenderName,
                    category.labelBrandSizeGroupSizes
                )?.exportName
            )
        );
    const sizeSystemsNeedBrandSave =
        showMappingStep && allCategoriesMapped && allSizeSystemsMapped && !allSizeSystemsSavedForBrand;
    const showSaveWarning =
        showMappingStep && !showNoDataMessage && (hasChecked || !allCategoriesMapped || !allSizeSystemsMapped);
    const showDefaultSizeSystemsMessage = !showSaveWarning && sizeSystemsNeedBrandSave;
    let saveFeedbackMessage = null;
    if (hasGroupedMappingConflicts) saveFeedbackMessage = GROUPED_MAPPING_CONFLICT_MESSAGE;
    else if (showSaveWarning) saveFeedbackMessage = INCOMPLETE_MAPPINGS_MESSAGE;
    else if (showDefaultSizeSystemsMessage) saveFeedbackMessage = DEFAULT_SIZE_SYSTEMS_MESSAGE;
    const isSaveEnabled =
        showMappingStep &&
        !hasChecked &&
        allCategoriesMapped &&
        allSizeSystemsMapped &&
        !hasGroupedMappingConflicts &&
        !hasSizeCountIssues &&
        (isDirty || categoryMappingsNeedConfirmation || sizeSystemsNeedBrandSave || sizeGroupMetadataNeedsSave);
    const isBulkChangeBtnEnabled = bulkChangeBtnEnabled && hasChecked;
    // #endregion

    // #region METHODS
    const handleCloseClicked = () => {
        if (forceConfirmSourceField) {
            setForceConfirmSourceField(false);
            return;
        }

        setSelectedSourceField(null);
        setConfirmedSourceField(null);
        setDraftCategoryMappings([]);
        setBulkCategoryOption(null);
        setBulkChangeBtnEnabled(false);
        onClose?.();
    };

    const handleSelectSourceClicked = () => {
        if (!sourceFieldSelection) return;
        setForceConfirmSourceField(false);
        setConfirmedSourceField(sourceFieldSelection);
        setSelectedSourceField(sourceFieldSelection);
    };

    const handleBulkAdd = () => {
        if (!isBulkChangeBtnEnabled) return;

        setDraftCategoryMappings(prev =>
            prev.map(category => {
                if (!category.isChecked) return category;
                return { ...buildCategoryDraftFromOption(category, bulkCategoryOption), isChecked: false };
            })
        );
        setBulkCategoryOption(null);
        setBulkChangeBtnEnabled(false);
    };

    const handleGroupChange = (groupKey, updateMapping) => {
        setDraftCategoryMappings(prev => updateCategoryMappingGroup(prev, groupKey, updateMapping));
    };

    const onSaveClicked = () => {
        if (!isSaveEnabled || isSaving) return;

        setIsSaving(true);
        setTimeout(() => {
            setCategoryFieldName?.(effectiveCategoryFieldName);
            setCategoryMappings?.(
                draftCategoryMappings.map(category => ({
                    labelBrandCategoryName: category.labelBrandCategoryName,
                    labelBrandGenderName: category.labelBrandGenderName,
                    labelBrandSizeGroupSizes: category.labelBrandSizeGroupSizes,
                    exportName: category.exportName,
                    subCategoryExportName: category.subCategoryExportName ?? "",
                    completeCategoryNameFromPOS: category.completeCategoryNameFromPOS ?? "",
                    relatedSizeGroupIdFromPOS: category.relatedSizeGroupIdFromPOS ?? null,
                    relatedSizeGroupNameFromPOS: category.relatedSizeGroupNameFromPOS ?? ""
                }))
            );
            setSaveButtonEnabled?.(true);
            setIsSaving(false);
            handleCloseClicked();
        }, 500);
    };
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        if (!show) return;

        setSelectedSourceField(null);
        setConfirmedSourceField(savedSourceField);
        setDraftCategoryMappings([]);
        setBulkCategoryOption(null);
        setBulkChangeBtnEnabled(false);
    }, [savedSourceField, show]);

    useEffect(() => {
        if (!show || !effectiveCategoryFieldName) return;
        setDraftCategoryMappings(sourceCategoryRows);
    }, [effectiveCategoryFieldName, show, sourceCategoryRows]);
    // #endregion

    // #region RENDER
    if (showSourcePreview) {
        return (
            <FullScreenOverlay show={show}>
                <POSSourcePreview
                    show={showSourcePreview}
                    labelBrandID={labelBrandID}
                    onCloseClick={() => setShowSourcePreview(false)}
                />
            </FullScreenOverlay>
        );
    }

    if (showSourceStep) {
        return (
            <FullScreenOverlay show={show}>
                <StyledCategorySourceStep>
                    <div className="source-shell">
                        <div className="attention-label">ATTENTION!!</div>
                        <AlertTriangle className="source-icon" />
                        <div className="source-title">Choose category source field</div>
                        <div className="source-description">
                            Which field in this Label Brand should BOtag use to map products to your default POS
                            categories?
                        </div>
                        <div className="source-helper">
                            Choose the source field that best represents product category, product type, group,
                            department, or family. You can review the file before selecting.
                        </div>
                        <BtnLargeSolid
                            title="VIEW SOURCE FILE"
                            normalColor="var(--Buttons-All-Platforms-Gray-500)"
                            hoverColor="var(--Buttons-All-Platforms-Gray-500)"
                            hoverBorderColor="var(--text-icons-orange-500)"
                            textStyle={{
                                fontSize: "12px",
                                textTransform: "uppercase"
                            }}
                            style={{
                                width: "auto",
                                height: "35px",
                                padding: "0 10px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center"
                            }}
                            onClick={() => setShowSourcePreview(true)}
                        />
                        <div className="source-dropdown">
                            <FormField
                                type="dropdown"
                                dropdownWithinViewport={true}
                                varName="category-source-field"
                                styling={{
                                    formFieldWidth: "100%",
                                    formFieldHeight: "37px",
                                    marginBottom: "0px",
                                    dropdownOverlayTop: "45px"
                                }}
                                placeholder="Select source field"
                                isDropdownMultiselect={false}
                                isDropdownSearchable={true}
                                dropdownData={SOURCE_FIELD_OPTIONS}
                                value={sourceFieldSelection?.name ?? ""}
                                onDropdownChange={item => setSelectedSourceField(item)}
                                requiredType="simple"
                                useDropdownPortal={true}
                            />
                        </div>
                        <div className="source-actions pt-16px">
                            <BtnLargeSolid
                                title="CANCEL"
                                onClick={handleCloseClicked}
                                normalColor="var(--Buttons-All-Platforms-Gray-500)"
                                hoverColor="var(--Buttons-All-Platforms-Gray-500)"
                                hoverBorderColor="var(--text-icons-orange-500)"
                                textStyle={{
                                    fontSize: "12px",
                                    textTransform: "uppercase"
                                }}
                                style={{
                                    width: "100%",
                                    height: "35px",
                                    padding: "0 10px",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center"
                                }}
                            />
                            <BtnLargeSolid
                                title="SELECT"
                                onClick={handleSelectSourceClicked}
                                disabled={!sourceFieldSelection}
                                normalColor="var(--buttons-all-platforms-gray-500, #10141b)"
                                hoverColor="var(--buttons-all-platforms-gray-500, #10141b)"
                                disabledColor="var(--buttons-all-platforms-gray-500, #10141b)"
                                textStyle={{
                                    fontSize: "12px",
                                    textTransform: "uppercase",
                                    color: !sourceFieldSelection
                                        ? "var(--text-and-icons-gray-300, #4f5b6d)"
                                        : "var(--text-and-icons-orange-500, #ff9900)"
                                }}
                                style={{
                                    width: "100%",
                                    height: "35px",
                                    padding: "0 10px",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center"
                                }}
                            />
                        </div>
                    </div>
                </StyledCategorySourceStep>
            </FullScreenOverlay>
        );
    }

    return (
        <FullScreenOverlay show={show}>
            <StyledPOSCategoriesTablePopup className="categories-table-popup">
                <div className="popup-shell">
                    <div className="popup-header">
                        <div className="header-title-container">
                            <List className="header-icon" />
                            <div className="header-title">CUSTOMIZE CATEGORIES</div>
                        </div>
                        <div className="header-actions">
                            <BtnLargeSolid
                                title="CLOSE"
                                onClick={handleCloseClicked}
                                normalColor="var(--Buttons-All-Platforms-Gray-500)"
                                hoverColor="var(--Buttons-All-Platforms-Gray-500)"
                                hoverBorderColor="var(--text-icons-orange-500)"
                                textStyle={{
                                    fontSize: "12px",
                                    textTransform: "uppercase"
                                }}
                                style={{
                                    width: "67px",
                                    height: "30px",
                                    padding: "0 10px",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center"
                                }}
                            />
                            <BtnLargeSolid
                                title={isSaving ? <SimpleLoadingSpinner /> : "SAVE"}
                                onClick={onSaveClicked}
                                disabled={!isSaveEnabled || isSaving}
                                normalColor="var(--buttons-all-platforms-orange-500, #FF9800)"
                                hoverColor="var(--buttons-all-platforms-orange-400, #FF9800)"
                                disabledColor="var(--buttons-all-platforms-gray-500, #10141b)"
                                textStyle={{
                                    fontSize: "12px",
                                    textTransform: "uppercase",
                                    color:
                                        !isSaveEnabled || isSaving
                                            ? "var(--text-and-icons-gray-300, #4f5b6d)"
                                            : "#ffffff"
                                }}
                                style={{
                                    width: "67px",
                                    height: "30px",
                                    padding: "0 10px",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center"
                                }}
                            />
                        </div>
                    </div>

                    {showNoDataMessage && (
                        <div className="empty-categories-state">
                            <div className="empty-categories-title">
                                <AlertCircle className="empty-categories-icon" />
                                <span>NO DATA YET</span>
                            </div>
                            <div className="empty-categories-description">
                                This could be a new brand in BOtag. Once the first items from this brand are available,
                                you can link your standard Categories to the brand's listed categories.
                            </div>
                        </div>
                    )}

                    {showMappingStep && (
                        <>
                            <div className="popup-content">
                                <div className="bulk-action-section">
                                    <div className="bulk-action-row">
                                        <div
                                            className="bulk-label"
                                            style={{
                                                color: hasChecked
                                                    ? "var(--text-and-icons-gray-100, #d1d1d1)"
                                                    : "var(--text-and-icons-gray-300, #4f5b6d)"
                                            }}>
                                            Map selected Brand categories to POS category:
                                        </div>
                                        <FormField
                                            type="dropdown"
                                            dropdownWithinViewport={true}
                                            varName="category-bulk-change"
                                            styling={{
                                                formFieldWidth: "100%",
                                                formFieldHeight: "27px",
                                                marginBottom: "0px",
                                                dropdownOverlayTop: "35px"
                                            }}
                                            placeholder="Select yours"
                                            isDropdownMultiselect={false}
                                            isDropdownSearchable={true}
                                            dropdownData={posCategoryOptions}
                                            value={hasChecked ? (bulkCategoryOption?.name ?? "") : ""}
                                            onDropdownChange={item => {
                                                setBulkCategoryOption(item);
                                                setBulkChangeBtnEnabled(true);
                                            }}
                                            requiredType={hasChecked ? "simple" : "disabledSimple"}
                                        />
                                        <div
                                            className={`bulk-add-btn ${isBulkChangeBtnEnabled ? "enabled cursor-pointer" : "disabled hover-not-allowed"}`}
                                            onClick={isBulkChangeBtnEnabled ? handleBulkAdd : null}>
                                            <Plus
                                                className="plus"
                                                color={
                                                    isBulkChangeBtnEnabled
                                                        ? "#FFFFFF"
                                                        : "var(--text-and-icons-gray-300, #7C868B)"
                                                }
                                            />
                                        </div>
                                    </div>

                                    {hasSizeCountIssues ? (
                                        <div className="category-size-count-message" role="alert">
                                            <AlertCircle className="category-size-count-icon" aria-hidden="true" />
                                            <span>
                                                Some brand's size groups have more sizes than their assigned POS Size
                                                System supports. Choose a POS Size System with enough sizes for every
                                                brand's size group.
                                            </span>
                                        </div>
                                    ) : (
                                        saveFeedbackMessage && <div className="save-warning">{saveFeedbackMessage}</div>
                                    )}

                                    <div
                                        style={{
                                            display: "flex",
                                            gap: "16px",
                                            width: "100%",
                                            justifyContent: "center"
                                        }}>
                                        <BtnLargeSolid
                                            title="VIEW SOURCE FILE"
                                            normalColor="var(--Buttons-All-Platforms-Gray-500)"
                                            hoverColor="var(--Buttons-All-Platforms-Gray-500)"
                                            hoverBorderColor="var(--text-icons-orange-500)"
                                            textStyle={{
                                                fontSize: "12px",
                                                textTransform: "uppercase"
                                            }}
                                            style={{
                                                width: "auto",
                                                height: "35px",
                                                padding: "0 10px",
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center"
                                            }}
                                            onClick={() => setShowSourcePreview(true)}
                                        />

                                        <BtnLargeSolid
                                            title="CHOOSE CATEGORY SOURCE"
                                            normalColor="var(--Buttons-All-Platforms-Gray-500)"
                                            hoverColor="var(--Buttons-All-Platforms-Gray-500)"
                                            hoverBorderColor="var(--text-icons-orange-500)"
                                            textStyle={{
                                                fontSize: "12px",
                                                textTransform: "uppercase"
                                            }}
                                            style={{
                                                width: "auto",
                                                height: "35px",
                                                padding: "0 10px",
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center"
                                            }}
                                            onClick={() => {
                                                setForceConfirmSourceField(true);
                                                setSelectedSourceField(confirmedSourceField || savedSourceField);
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className="categories-grid-row">
                                    <div className="checkbox-cell">
                                        <CheckboxVector
                                            checkboxColor="#00F260"
                                            onCheckboxClick={() => {
                                                const newValue = !allChecked;
                                                setDraftCategoryMappings(prev =>
                                                    prev.map(category => ({ ...category, isChecked: newValue }))
                                                );
                                            }}
                                            isChecked={allChecked}
                                        />
                                    </div>
                                    <div className="header-label">
                                        Brand's category
                                        <TooltipWithInfoCircle
                                            iconSize={"14px"}
                                            enabled={true}
                                            header="Brand's Category"
                                            title={
                                                <>
                                                    These are the category values found in the BOtag product data, based
                                                    on the source field you selected before opening this popup.
                                                    <br />
                                                    <br />
                                                    Each brand category appears once per assigned POS gender and brand
                                                    size group. Brand genders with the same POS gender and connected
                                                    sizes share one row; changes apply to all of them.
                                                    <br />
                                                    <br />
                                                    This does not change the original BOtag category data. It only
                                                    defines how these products should be categorized when exported to
                                                    Front Systems.
                                                </>
                                            }
                                            width="350px"
                                        />
                                    </div>
                                    <div className="header-label">
                                        POS gender
                                        <TooltipWithInfoCircle
                                            iconSize={"14px"}
                                            enabled={true}
                                            header="POS Gender"
                                            title={
                                                <>
                                                    Shows the POS gender connected to this category row.
                                                    <br />
                                                    <br />
                                                    The same brand category appears on separate rows when its assigned
                                                    POS genders or size groups differ. Multiple brand genders mapped to
                                                    the same POS gender are combined when their size groups overlap.
                                                    <br />
                                                    <br />
                                                    This helps BOtag link each category and gender combination to the
                                                    correct POS category and size system.
                                                </>
                                            }
                                            width="350px"
                                        />
                                    </div>
                                    <div className="header-label">
                                        Brand's Size Group
                                        <TooltipWithInfoCircle
                                            enabled={true}
                                            iconSize="14px"
                                            header="Brand's Size Group"
                                            title="Products with overlapping sizes share a group, including smaller subsets and groups connected through another product. Products with no shared sizes appear on separate rows. Hover over a group to see all its sizes."
                                            width="400px"
                                        />
                                    </div>
                                    <div className="header-label">
                                        Categories to POS
                                        <TooltipWithInfoCircle
                                            iconSize={"14px"}
                                            enabled={true}
                                            header="Categories to POS"
                                            title={
                                                <>
                                                    Select which Front Systems POS category this brand category should
                                                    be exported as.
                                                    <br />
                                                    <br />
                                                    The dropdown shows the POS categories managed from My Defaults
                                                    Categories and synchronized with Front Systems.
                                                    <br />
                                                    <br />
                                                    Each brand category must be linked to a POS category before the
                                                    category rules can be saved. Categories without a default Size
                                                    System can still be selected and completed in the next column.
                                                </>
                                            }
                                            width="350px"
                                        />
                                    </div>
                                    <div className="header-label">
                                        POS Size System
                                        <TooltipWithInfoCircle
                                            iconSize={"14px"}
                                            enabled={true}
                                            header="POS Size System"
                                            title={
                                                <>
                                                    Select which Front Systems Size System this brand category should
                                                    use.
                                                    <br />
                                                    <br />
                                                    When the POS category has a Size System linked in My Defaults, it is
                                                    selected here automatically as the default.
                                                    <br />
                                                    <br />
                                                    You can choose a different Size System for this Label Brand without
                                                    changing the link stored in My Defaults.
                                                    <br />
                                                    <br />
                                                    If the selected category has no default, this field shows a dash
                                                    until you choose one. Every row needs both a POS category and Size
                                                    System before these rules can be saved.
                                                </>
                                            }
                                            width="350px"
                                        />
                                    </div>
                                </div>
                                <div className="divider-2"></div>
                            </div>

                            <div className="table-scroll-area">
                                <div className="table-body">
                                    {groupedCategoryMappings.map(
                                        (
                                            {
                                                key,
                                                category,
                                                sourceIndices,
                                                hasCategoryConflict,
                                                hasSizeSystemConflict
                                            },
                                            idx
                                        ) => (
                                            <div className={`table-item ${idx % 2 === 0 ? "even" : ""}`} key={key}>
                                                <div className="categories-grid-row">
                                                    <div className="checkbox-cell">
                                                        <CheckboxVector
                                                            checkboxColor="#00F260"
                                                            onCheckboxClick={() =>
                                                                handleGroupChange(key, item => ({
                                                                    ...item,
                                                                    isChecked: !category.isChecked
                                                                }))
                                                            }
                                                            isChecked={category.isChecked || false}
                                                        />
                                                    </div>
                                                    <div className="item-name field-brands-category text-one-line">
                                                        {category.labelBrandCategoryName}
                                                    </div>
                                                    <div className="item-name field-pos-gender">
                                                        {category.posGender}
                                                    </div>
                                                    <TooltipSimple
                                                        variant="table-cell"
                                                        overlayStyle={{ maxWidth: "600px" }}
                                                        title={
                                                            <div
                                                                style={{
                                                                    whiteSpace: "normal",
                                                                    wordBreak: "break-word"
                                                                }}>
                                                                {formatSizeGroupSizes(
                                                                    category.labelBrandSizeGroupSizes
                                                                ) || "No sizes provided"}
                                                            </div>
                                                        }>
                                                        <span className="item-name brand-size-group">
                                                            {formatSizeGroupSizes(category.labelBrandSizeGroupSizes) ||
                                                                "—"}
                                                        </span>
                                                    </TooltipSimple>
                                                    <FormField
                                                        type="dropdown"
                                                        dropdownWithinViewport={true}
                                                        varName={`category-dropdown-${idx}`}
                                                        styling={{
                                                            formFieldWidth: "100%",
                                                            formFieldHeight: "27px",
                                                            marginBottom: "0px",
                                                            dropdownOverlayTop: "35px",
                                                            dropdownOverlayWidth: "250px"
                                                        }}
                                                        useDropdownPortal={true}
                                                        placeholder={
                                                            hasCategoryConflict
                                                                ? "Select for all brand genders"
                                                                : "Select yours"
                                                        }
                                                        isDropdownMultiselect={false}
                                                        isDropdownSearchable={true}
                                                        dropdownData={posCategoryOptions}
                                                        value={category.completeCategoryNameFromPOS}
                                                        onDropdownChange={item =>
                                                            handleGroupChange(key, categoryItem =>
                                                                buildCategoryDraftFromOption(categoryItem, item)
                                                            )
                                                        }
                                                        requiredType="none"
                                                    />
                                                    <div className="category-size-system-cell">
                                                        <div className="category-size-system-field">
                                                            <FormField
                                                                type="dropdown"
                                                                dropdownWithinViewport={true}
                                                                varName={`size-system-dropdown-${idx}`}
                                                                styling={{
                                                                    formFieldWidth: "100%",
                                                                    formFieldHeight: "27px",
                                                                    marginBottom: "0px",
                                                                    dropdownOverlayTop: "35px",
                                                                    dropdownOverlayWidth: "210px"
                                                                }}
                                                                useDropdownPortal={true}
                                                                placeholder={
                                                                    hasSizeSystemConflict
                                                                        ? "Select for all brand genders"
                                                                        : "-"
                                                                }
                                                                isDropdownMultiselect={false}
                                                                isDropdownSearchable={true}
                                                                dropdownData={sizeSystemOptions}
                                                                value={category.relatedSizeGroupNameFromPOS ?? ""}
                                                                onDropdownChange={item =>
                                                                    handleGroupChange(key, categoryItem =>
                                                                        buildCategoryDraftFromSizeSystem(
                                                                            categoryItem,
                                                                            item
                                                                        )
                                                                    )
                                                                }
                                                                requiredType={
                                                                    category.completeCategoryNameFromPOS ||
                                                                    category.exportName
                                                                        ? "none"
                                                                        : "disabledSimple"
                                                                }
                                                            />
                                                        </div>
                                                        {sourceIndices.some(index => sizeCountIssues[index]) && (
                                                            <TooltipSimple
                                                                header="Problem"
                                                                width="500px"
                                                                title={
                                                                    <div className="category-size-count-details">
                                                                        {getSizeCountIssueTooltip(
                                                                            category,
                                                                            sourceIndices
                                                                                .map(index => sizeCountIssues[index])
                                                                                .find(Boolean),
                                                                            effectiveCategoryFieldName
                                                                        )}
                                                                        <TooltipTitle style={{ margin: "8px 0 10px" }}>
                                                                            Action
                                                                        </TooltipTitle>
                                                                        <div>
                                                                            Choose a POS Size System with enough sizes
                                                                            to cover this brand's size group.
                                                                        </div>
                                                                        <TooltipTitle style={{ margin: "8px 0 10px" }}>
                                                                            NOTE
                                                                        </TooltipTitle>
                                                                        <div>
                                                                            If the selected POS Size System does not
                                                                            contain the exact size names in the Brand's
                                                                            Size Group, you can associate each brand
                                                                            size with a POS size in the CUSTOMIZE SIZES
                                                                            popup.
                                                                        </div>
                                                                    </div>
                                                                }>
                                                                <AlertCircle
                                                                    className="category-size-count-icon"
                                                                    role="img"
                                                                    aria-label="POS Size System has too few sizes"
                                                                />
                                                            </TooltipSimple>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </StyledPOSCategoriesTablePopup>
        </FullScreenOverlay>
    );
    // #endregion
}
